import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { API_URLS } from '../constants';
import { authenticatedFetch } from '../utils/api';

const VerifyEmailUpdatePage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [verificationCode, setVerificationCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [countdown, setCountdown] = useState(0);
  const [newEmail, setNewEmail] = useState('');

  useEffect(() => {
    // Get newEmail from navigation state
    if (location.state?.newEmail) {
      setNewEmail(location.state.newEmail);
    } else {
      // If no email in state, redirect back to update email page
      navigate('/update-email');
    }
  }, [location.state, navigate]);

  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (countdown > 0) {
      timer = setTimeout(() => setCountdown(countdown - 1), 1000);
    }
    return () => clearTimeout(timer);
  }, [countdown]);

  const handleVerifyCode = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!verificationCode || verificationCode.length !== 6) {
      setMessage({ type: 'error', text: 'Please enter a 6-digit verification code' });
      return;
    }

    setIsLoading(true);
    setMessage(null);

    try {
      const response = await authenticatedFetch(API_URLS.EMAIL_UPDATE.VERIFY, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ code: verificationCode })
      });

      if (response.ok) {
        setMessage({ type: 'success', text: 'Email updated successfully!' });
        setTimeout(() => {
          navigate('/settings');
        }, 2000);
      } else {
        const errorData = await response.json();
        // Handle both old and new error response formats
        const errorMessage = errorData.error?.message || errorData.message || 'Invalid verification code';
        setMessage({ type: 'error', text: errorMessage });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to verify code' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendCode = async () => {
    if (countdown > 0) return;

    setIsLoading(true);
    setMessage(null);

    try {
      const response = await authenticatedFetch(API_URLS.EMAIL_UPDATE.SEND_VERIFICATION, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ newEmail })
      });

      if (response.ok) {
        setMessage({ type: 'success', text: 'Verification code resent successfully' });
        setCountdown(60); // 60 second cooldown
        
        // In development, show the code for testing
        const data = await response.json();
        if (data.debug?.code) {
          setMessage({ 
            type: 'success', 
            text: `Code resent! (Dev mode: ${data.debug.code})` 
          });
        }
      } else {
        const errorData = await response.json();
        // Handle both old and new error response formats  
        const errorMessage = errorData.error?.message || errorData.message || 'Failed to resend code';
        setMessage({ type: 'error', text: errorMessage });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to resend code' });
    } finally {
      setIsLoading(false);
    }
  };

  if (!newEmail) {
    return (
      <div className="min-h-screen bg-gray-50 py-4 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md mx-auto">
          <div className="bg-white shadow-sm rounded-lg border border-gray-200 p-6">
            <p className="text-gray-600">Loading...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-4 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md mx-auto">
        <div className="bg-white shadow-sm rounded-lg border border-gray-200 p-6">
          <div className="mb-6">
            <h1 className="text-2xl font-semibold text-gray-900 mb-2">Verify Email Address</h1>
            <p className="text-gray-600">
              We've sent a verification code to <strong>{newEmail}</strong>. 
              Please enter the 6-digit code below to complete the email update.
            </p>
          </div>

          {message && (
            <div className={`p-4 rounded-md mb-6 ${
              message.type === 'success'
                ? 'bg-green-50 border border-green-200 text-green-800'
                : 'bg-red-50 border border-red-200 text-red-800'
            }`}>
              {message.text}
            </div>
          )}

          <form onSubmit={handleVerifyCode} className="space-y-4">
            <div>
              <label htmlFor="verificationCode" className="block text-sm font-medium text-gray-700 mb-1">
                Verification Code
              </label>
              <input
                type="text"
                id="verificationCode"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-brand-electric-blue focus:border-brand-electric-blue text-center text-lg tracking-widest"
                placeholder="000000"
                maxLength={6}
                required
              />
            </div>

            <div className="flex space-x-3">
              <button
                type="submit"
                disabled={isLoading}
                className="flex-1 px-4 py-2 bg-brand-dark-blue text-white text-sm font-medium rounded-md hover:bg-brand-electric-blue focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-electric-blue disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? 'Verifying...' : 'Verify Code'}
              </button>
              <button
                type="button"
                onClick={() => navigate('/update-email')}
                className="px-4 py-2 bg-gray-200 text-gray-700 text-sm font-medium rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
              >
                Back
              </button>
            </div>
          </form>

          <div className="mt-6 pt-6 border-t border-gray-200">
            <p className="text-sm text-gray-600 mb-3">
              Didn't receive the code?
            </p>
            <button
              type="button"
              onClick={handleResendCode}
              disabled={countdown > 0 || isLoading}
              className="w-full px-4 py-2 bg-gray-100 text-gray-700 text-sm font-medium rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {countdown > 0 
                ? `Resend in ${countdown}s` 
                : 'Resend Code'
              }
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VerifyEmailUpdatePage;